import { useMemo, useState, useCallback } from 'react';

import useTbbPaymentConfig from '../../../../../../hooks/useTbbPaymentConfig';
import { Button, BUTTON_SIZE, BUTTON_TYPE } from '../../../../global/Button';
import InfoSection from '../../../../global/InfoSection';
import LoadingBar from '../../../../global/LoadingBar';
import {
  getNextButtonStyles,
  TBB_CREDIT_AMOUNT_TAG,
  TBB_PARTNER_TAG,
  TBB_PAYMENT_CONFIG_ITEMS,
} from '../../../../utils/tbbRegistration';
import PerksCard from '../../Components/PerksCard';
import GiftCardsCard from '../../Components/GiftCardsCard';
import Dialog from '../../../../global/Mui/Dialog';
import CircleQuestionMarkSvg from '../../../../global/SvgComponents/CircleQuestionMark_svg';
import CloseSvg from '../../../../global/SvgComponents/Close_svg';
import checkIcon from '../../../../images/check-circle-green-payonship.svg';
import bagIcon from '../../../../images/icon_tbb.png';
import qrCodeIcon from '../../../../images/qrcode.png';
import tcIcon from '../../../../images/trashie-cash-v3.svg';

import './Service.scss';

const Service = ({
  creditAmt,
  storeConfig,
  onSubmit,
  stepTrackerProps,
  isPayOnShip = false,
  isLoadingPayOnShipConfig = false,
  payOnShipConfig = {},
}) => {
  const { isLoading: isLoadingTbbPaymentConfig, tbbPaymentConfig } = useTbbPaymentConfig();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleOpenDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const items = useMemo(
    () => tbbPaymentConfig.items?.map((item) => {
      const title = item.titleService?.replace(TBB_PARTNER_TAG, storeConfig?.title);
      const description = item.description
        ?.replace(TBB_PARTNER_TAG, storeConfig?.title)
        .replace(TBB_CREDIT_AMOUNT_TAG, creditAmt);

      switch (item.id) {
        case TBB_PAYMENT_CONFIG_ITEMS.recycling: {
          const perksItems = item.children
            ?.filter(({ serviceDisabled }) => !serviceDisabled)
            .map((child) => ({
              key: child.id,
              title: child.titleService,
              description: child.serviceDescription,
            })) || [];

          return {
            key: item.id,
            description,
            content: (
              <PerksCard
                title={`1. ${title}`}
                items={perksItems}
              />
            ),
          };
        }
        case TBB_PAYMENT_CONFIG_ITEMS.gift_cards:
          // Skip GiftCardsCard when isPayOnShip is true
          if (isPayOnShip) {
            return null;
          }
          return {
            key: item.id,
            title,
            description,
            content: (
              <GiftCardsCard title={`2. ${title}`} />
            ),
          };
        default:
          return null;
      }
    }).filter(Boolean),
    [tbbPaymentConfig, storeConfig, isPayOnShip],
  );

  const isLoadingInfo = isLoadingTbbPaymentConfig || (isPayOnShip && isLoadingPayOnShipConfig);

  return (
    <div className={`RegisterService ${isPayOnShip ? 'RegisterService--payOnShip' : ''}`}>
      <InfoSection
        titleText="THE TAKE BACK BAG"
        subtitle={isPayOnShip ? (
          <>
            <span>Say hello to the easiest way to clean</span>
            <span>out, donate your unwanted clothing,</span>
            <span>and earn points.</span>
          </>
        ) : 'Say hello to the easiest way to clean out, donate your unwanted clothing, and earn points. '}
        titleStyles={`textTrasformNone ${isPayOnShip ? 'RegisterService__title--centered' : ''}`}
        subtitleStyles={`RegisterService__subtitle ${isPayOnShip ? 'RegisterService__subtitle--centered' : ''}`}
      />
      {isLoadingInfo ? (
        <LoadingBar />
      ) : (
        <>
          {isPayOnShip ? (
            <div className="RegisterService__payOnShipCard">
              <div className="RegisterService__payOnShipCard-title">What you get:</div>

              {/* 1 BAG */}
              <div className="RegisterService__payOnShipCard-row">
                <div className="RegisterService__payOnShipCard-iconWrap RegisterService__payOnShipCard-iconWrap--bag">
                  <img src={bagIcon} alt="bag" className="RegisterService__payOnShipCard-iconImg" />
                  <img src={checkIcon} alt="check" className="RegisterService__payOnShipCard-check" />
                </div>
                <div className="RegisterService__payOnShipCard-text">
                  <div className="RegisterService__payOnShipCard-text-title">1 BAG</div>
                  <div className="RegisterService__payOnShipCard-text-desc">
                    Donate up-to 15 lbs of clothing, shoes and more
                  </div>
                </div>
                <Button
                  type={BUTTON_TYPE.LINK}
                  size={BUTTON_SIZE.SMALL}
                  onClick={handleOpenDialog}
                  className="RegisterService__payOnShipCard-infoBtn"
                >
                  <CircleQuestionMarkSvg width={24} height={24} />
                </Button>
              </div>

              {/* Separator */}
              <div className="RegisterService__payOnShipCard-separator">
                <div className="RegisterService__payOnShipCard-separator-line" />
                <div className="RegisterService__payOnShipCard-plus">+</div>
                <div className="RegisterService__payOnShipCard-separator-line" />
              </div>

              {/* 1 RETURN LABEL */}
              <div className="RegisterService__payOnShipCard-row">
                <div className="RegisterService__payOnShipCard-iconWrap">
                  <img src={qrCodeIcon} alt="qr-code" className="RegisterService__payOnShipCard-iconImg" />
                  <img src={checkIcon} alt="check" className="RegisterService__payOnShipCard-check" />
                </div>
                <div className="RegisterService__payOnShipCard-text">
                  <div className="RegisterService__payOnShipCard-text-title">1 RETURN LABEL</div>
                  <div className="RegisterService__payOnShipCard-text-desc">
                    Prepaid QR code or print-able shipping label
                  </div>
                </div>
              </div>

              {/* Separator */}
              <div className="RegisterService__payOnShipCard-separator">
                <div className="RegisterService__payOnShipCard-separator-line" />
                <div className="RegisterService__payOnShipCard-plus">+</div>
                <div className="RegisterService__payOnShipCard-separator-line" />
              </div>

              {/* 3000 POINTS */}
              <div className="RegisterService__payOnShipCard-row">
                <div className="RegisterService__payOnShipCard-iconWrap RegisterService__payOnShipCard-iconWrap--tc">
                  <img src={tcIcon} alt="tc" className="RegisterService__payOnShipCard-iconImg" />
                  <img src={checkIcon} alt="check" className="RegisterService__payOnShipCard-check" />
                </div>
                <div className="RegisterService__payOnShipCard-text">
                  <div className="RegisterService__payOnShipCard-text-title">{`${payOnShipConfig.PAY_ON_SHIP_REWARDS_POINTS} POINTS`}</div>
                  <div className="RegisterService__payOnShipCard-text-desc">
                    TrashieCash™ rewards points.
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="RegisterService__container">
              {items.map(({
                key,
                content,
              }) => (
                <div className="RegisterService__container--item" key={key}>
                  <div className="RegisterService__container--item-content">
                    {content}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="RegisterService__container--cta">
            <Button
              type={BUTTON_TYPE.QUATERNARY}
              size={BUTTON_SIZE.LARGE}
              onClick={onSubmit}
              style={getNextButtonStyles(storeConfig)}
              trackerProps={stepTrackerProps}
            >
              {isPayOnShip ? 'NEXT' : 'I WANT TO RECYCLE!'}
            </Button>
          </div>

          {/* Dialog for "We accept" / "We do not accept" */}
          <Dialog open={isDialogOpen} onClose={handleCloseDialog} closable>
            <div className="RegisterService__acceptDialog">
              <Button
                type={BUTTON_TYPE.LINK}
                size={BUTTON_SIZE.SMALL}
                onClick={handleCloseDialog}
                className="RegisterService__acceptDialog-closeBtn"
              >
                <CloseSvg />
              </Button>
              <div className="RegisterService__acceptDialog-section">
                <div className="RegisterService__acceptDialog-header">
                  <span>We accept</span>
                </div>
                <ul className="RegisterService__acceptDialog-list">
                  <li>Clothing & apparel</li>
                  <li>Shoes</li>
                  <li>Socks & clean underwear</li>
                  <li>Bags (handbags, purses, backpacks)</li>
                  <li>Outerwear</li>
                  <li>Swimwear</li>
                  <li>Uniforms</li>
                  <li>Ski gear</li>
                  <li>Costumes</li>
                  <li>Hats, gloves & scarves</li>
                  <li>Jewelry & accessories</li>
                  <li>Stuffed animals</li>
                  <li>Home textiles (blankets, sheets, towels)</li>
                </ul>
              </div>

              <div className="RegisterService__acceptDialog-section RegisterService__acceptDialog-section--reject">
                <div className="RegisterService__acceptDialog-header">
                  <span>We do not accept</span>
                </div>
                <ul className="RegisterService__acceptDialog-list">
                  <li>Clothing with paint, mold, or motor oil stains</li>
                  <li>Soiled kitchen & table linens (dish towels, napkins, etc.)</li>
                  <li>Placemats, oven mitts & aprons</li>
                  <li>Pillows, heated blankets & mattress pads</li>
                  <li>Fabric scraps (half-garments, cutoffs)</li>
                  <li>Face masks</li>
                  <li>Pet items (beds, leashes, etc.)</li>
                  <li>Anything soiled with bio material (human/pet)</li>
                </ul>
              </div>
            </div>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default Service;
